import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import { LoggedOutHeader } from "@/components/LoggedOutHeader";
import { StoreBadges } from "@/components/shared/StoreBadges";
import type { StoreLinkSurface } from "@/lib/storeLinks";
import { XLogo, TikTokLogo } from "@/components/shared/PlatformIcon";
import { SOCIAL_LINKS } from "@/constants/links";
import { BLOG, FAQ, PRIVACY, TERMS } from "@/routes";

const FOOTER_NAV = [
  { label: "Blog", to: BLOG() },
  { label: "FAQ", to: FAQ() },
  { label: "Privacy Policy", to: PRIVACY() },
  { label: "Terms & Conditions", to: TERMS() },
];

const SOCIAL_ICONS = [
  { href: SOCIAL_LINKS.INSTAGRAM, label: "Follow us on Instagram", icon: <Instagram size={22} /> },
  { href: SOCIAL_LINKS.TWITTER, label: "Follow us on X", icon: <XLogo size={22} /> },
  { href: SOCIAL_LINKS.TIKTOK, label: "Follow us on TikTok", icon: <TikTokLogo size={22} /> },
  { href: SOCIAL_LINKS.FACEBOOK, label: "Follow us on Facebook", icon: <Facebook size={22} /> },
];

const MarketingFooter = ({ surface }: { surface: StoreLinkSurface }) => (
  <footer className="border-t border-foreground/10 bg-background w-full">
    <div className="page-frame py-8 md:py-10 flex flex-col gap-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex flex-col items-center md:items-start gap-2">
          <img alt="Stasht logo" src="/Stasht - Typographic - orange.png" className="h-8 w-auto dark:hidden" />
          <img alt="Stasht logo" src="/Stasht - Typographic - white.png" className="h-8 w-auto hidden dark:block" />
          <p className="text-sm text-foreground/70">We&apos;re just getting started.</p>
        </div>
        <div className="flex items-center gap-5">
          {SOCIAL_ICONS.map(social => (
            <a
              key={social.href}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center text-foreground transition"
              aria-label={social.label}
            >
              {social.icon}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <StoreBadges className="h-10" surface={surface} placement="footer" />
        </div>
      </div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 border-t border-foreground/10 pt-6 text-sm">
        <p className="font-semibold text-foreground">© {new Date().getFullYear()} Copyright. All Rights Reserved.</p>
        <nav className="flex flex-wrap items-center justify-center gap-3 md:gap-4 font-semibold text-foreground">
          {FOOTER_NAV.map(link => (
            <Link key={link.to} to={link.to} className="transition">
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  </footer>
);

/**
 * Shared shell for public marketing/content pages (blog, use-case pages,
 * comparisons): logged-out header on top, compact footer below. `surface`
 * attributes the header and footer store badges to the page family hosting
 * the layout.
 */
export const MarketingPageLayout = ({ surface, children }: { surface: StoreLinkSurface; children: ReactNode }) => (
  <div className="flex min-h-screen flex-col bg-background" style={{ paddingTop: "var(--sat)" }}>
    <LoggedOutHeader surface={surface} />
    <main className="flex-1 w-full">{children}</main>
    <MarketingFooter surface={surface} />
  </div>
);
